import { z } from 'zod';

/**
 * Media signal — the News & Social wire substrate contract (#65/#183 wave 3a,
 * migration 0069, `media_signals`). One signal = one hazard-matched media item
 * from the locked free tier (GDELT GKG / curated Canadian RSS / Bluesky public
 * search; 'x' reserved for the founder-activated paid tier — no adapter ships).
 *
 * This contract is the seam wave 3b builds its TWO operator surfaces on (the
 * panel News & Social tab behind the protected Alerts/Assists queues + the COP
 * left-rail tab), so it is deliberate about three things:
 *
 *   • HONEST GEO — `geo` is non-null ONLY when the source itself provided a
 *     city/region-level coordinate (GDELT does; RSS/Bluesky don't). `geoName`
 *     can exist WITHOUT a point (a country-level GDELT location keeps its
 *     label but never a centroid dot — a country centroid on a map would lie).
 *     Nothing is ever geocoded by guess.
 *   • PER-SOURCE ROWS + `dedupKey` — the same story from two sources stays two
 *     signals (corroboration #183 counts copies); `dedupKey` (canonical-url /
 *     uri hash) lets a surface group duplicates at read time.
 *   • NO PROMOTION SEMANTICS — a signal carries no Response Level, no urgency,
 *     no anomaly linkage. Promotion is operator-gated by design and that gate
 *     is built in 3b; this is the ambient raw wire (noise tolerated).
 *
 * `hazardKeywords` are canonical {@link HazardKey}s from {@link HAZARD_LEXICON}
 * (an EN+FR lexicon shipped as data, per the OLA bilingual note) — never the
 * literal matched strings, so an FR match and an EN match filter identically.
 */

export const MEDIA_SIGNAL_SOURCES = ['gdelt', 'rss', 'bluesky', 'x'] as const;
export type MediaSignalSource = (typeof MEDIA_SIGNAL_SOURCES)[number];

export const MEDIA_SIGNAL_LANGUAGES = ['en', 'fr', 'und'] as const;
export type MediaSignalLanguage = (typeof MEDIA_SIGNAL_LANGUAGES)[number];

/** Precision of a source-provided location. 'country' never carries a point. */
export const MEDIA_SIGNAL_GEO_KINDS = ['city', 'region', 'country'] as const;
export type MediaSignalGeoKind = (typeof MEDIA_SIGNAL_GEO_KINDS)[number];

/** Source-provided point + its precision. Present only for city/region. */
export const MediaSignalGeoSchema = z.object({
  lon: z.number().min(-180).max(180),
  lat: z.number().min(-90).max(90),
  kind: z.enum(['city', 'region']),
});
export type MediaSignalGeo = z.infer<typeof MediaSignalGeoSchema>;

/**
 * Attached media thumbnail (#361 media-post pass-through) — the upstream photo /
 * video-poster URL an adapter extracted from the item (RSS <enclosure>/media:content/
 * inline <img>, a Bluesky embed thumb, an X media attachment). Stored verbatim in
 * `attrs.media` (jsonb — NO migration) and projected onto the signal at read time,
 * exactly like {@link MediaSignal.authoritative} projects from `attrs.tier`. It is
 * DISPLAY chrome only: a thumbnail the wire card renders + links through to source.
 * `kind` distinguishes a still image from a video poster (a video is shown as its
 * poster frame, never auto-played). `url` is an absolute http(s) URL — never a
 * fabricated or relative path. Carries NO promotion semantics on its own.
 */
export const MediaSignalMediaSchema = z.object({
  url: z.string().url(),
  kind: z.enum(['image', 'video']),
});
export type MediaSignalMedia = z.infer<typeof MediaSignalMediaSchema>;

// ── Server-side story collapse (#563) ────────────────────────────────────────

/**
 * One member of a collapsed story (#563) — a single copy of the same story
 * (same `dedupKey`) carried by one source/outlet. The StoryCard's diversity chip
 * expands to this list (source · title · relTime · link). Trimmed to display
 * essentials (no body) — the lead signal carries the full detail.
 */
export const MediaSignalStoryMemberSchema = z.object({
  source: z.enum(MEDIA_SIGNAL_SOURCES),
  /** Display outlet / origin (RSS outlet, GDELT domain, Bluesky handle). */
  outlet: z.string(),
  title: z.string().nullable(),
  url: z.string().nullable(),
  publishedAt: z.string().nullable(),
  ingestedAt: z.string(),
  authoritative: z.boolean(),
});
export type MediaSignalStoryMember = z.infer<typeof MediaSignalStoryMemberSchema>;

/**
 * Story-collapse envelope (#563) attached to the LEAD signal of a `dedupKey`
 * group by the API. The wire collapses copies server-side (row_number() over the
 * dedup key, authoritative-then-recency), so a surface renders ONE card per story.
 *
 * `distinctOutlets` (corroboration breadth) is counted SEPARATELY from `copies`
 * (raw volume) — a story carried by five CBC regional feeds is 5 copies but 1
 * outlet, NOT five-way corroboration. The chip reads "N outlets · <lead> ×copies".
 */
export const MediaSignalStorySchema = z.object({
  /** Total copies in the dedup group (raw volume — NOT corroboration). */
  copies: z.number().int().positive(),
  /** Distinct outlets carrying the story (the honest corroboration breadth). */
  distinctOutlets: z.number().int().positive(),
  /** The lead member's outlet label (what the chip names). */
  leadOutlet: z.string(),
  /** Any member carries a media thumbnail (drives the 📸 chip on the story). */
  hasMedia: z.boolean(),
  /** The member list for the chip expansion (capped server-side). */
  members: z.array(MediaSignalStoryMemberSchema),
});
export type MediaSignalStory = z.infer<typeof MediaSignalStorySchema>;

/** Server data-freshness (#563) — drives the "● live · updated HH:MM" stamp and
 *  the "STALE · newest data N min old" badge. Server-authored: `serverTime` is the
 *  API's now(), `newestIngestedAt` the newest row the wire holds. The surface
 *  computes staleness from THESE (server truth), never from client fetch success. */
export const MediaSignalFreshnessSchema = z.object({
  serverTime: z.string(),
  newestIngestedAt: z.string().nullable(),
});
export type MediaSignalFreshness = z.infer<typeof MediaSignalFreshnessSchema>;

export const MediaSignalSchema = z.object({
  id: z.string().uuid(),
  source: z.enum(MEDIA_SIGNAL_SOURCES),
  url: z.string().nullable(),
  title: z.string().nullable(),
  /** Snippet / post text (bounded at ingest) — never the full article. */
  body: z.string().nullable(),
  language: z.enum(MEDIA_SIGNAL_LANGUAGES),
  /** ISO-8601, or null when the source doesn't say when it published. */
  publishedAt: z.string().nullable(),
  ingestedAt: z.string(),
  /** Honest source-provided point, or null (most rows — RSS/Bluesky). */
  geo: MediaSignalGeoSchema.nullable(),
  /** Location label; may be present WITHOUT a point (country-level). */
  geoName: z.string().nullable(),
  /**
   * Attached media thumbnail (#361) — projected from `attrs.media`. Null/absent
   * for a text-only item. DISPLAY chrome only (a thumbnail + click-through);
   * carries no promotion semantics. Optional + defaults absent so a pre-#361
   * literal stays valid.
   */
  media: MediaSignalMediaSchema.nullable().optional(),
  /** Canonical hazard keys (≥1 — only lexicon-matched items are stored). */
  hazardKeywords: z.array(z.string()),
  /** Cross-source grouping key — same story, same key, per-source rows. */
  dedupKey: z.string(),
  /**
   * AUTHORITATIVE provenance (#297 official-feed backstop). True for items from a
   * curated official government/agency feed (CCCS cyber alerts, GoC recalls, PHAC
   * notices) — the authoritative corroborating leg under the #296 social surge.
   * Surfaces route these to the Official lane (not "trending — not confirmed") and
   * count them as corroboration. Optional + defaults false: a plain news/social
   * item is non-authoritative. Carries NO promotion semantics — still operator-
   * gated, never auto-urgent.
   */
  authoritative: z.boolean().optional(),
  /**
   * DISPLAY-TIME SURGE GATE (#349). True when this geo-bearing, hazard-tagged
   * signal falls inside an OPEN igniter cluster (media_signal_anomalies) of a
   * matching hazard — i.e. it's part of a qualified surge, not a lone post.
   * The stored hazard tag is NEVER stripped (archive / official / LLM still see
   * it); this flag only governs WIRE SURFACING — the surge lane shows a clustered
   * post under its surge card and demotes a non-clustered post to raw chatter.
   * Geo-less posts (RSS / Bluesky / X) are never clustered → always false.
   * Optional + defaults false so a pre-#349 literal / the detail route stay valid.
   */
  inSurgeCluster: z.boolean().optional(),
  /** The cluster_key of the open cluster this signal belongs to (the join key the
   *  surge lane groups posts under), or null when not in a surge. */
  surgeClusterKey: z.string().nullable().optional(),
  /**
   * STORY COLLAPSE envelope (#563) — present on the LEAD signal of a `dedupKey`
   * group returned by the list route (which collapses copies server-side). Absent
   * on the detail route and on a pre-#563 literal (a lone signal is its own story).
   */
  story: MediaSignalStorySchema.optional(),
});
export type MediaSignal = z.infer<typeof MediaSignalSchema>;

export const MediaSignalListResponseSchema = z.object({
  signals: z.array(MediaSignalSchema),
  /** Server data-freshness (#563) — optional so a pre-#563 literal stays valid. */
  freshness: MediaSignalFreshnessSchema.optional(),
});
export type MediaSignalListResponse = z.infer<typeof MediaSignalListResponseSchema>;

/** Detail adds raw provenance (the verbatim upstream item) for the 3b card. */
export const MediaSignalDetailSchema = MediaSignalSchema.extend({
  sourceRecordId: z.string(),
  attrs: z.record(z.unknown()),
});
export type MediaSignalDetail = z.infer<typeof MediaSignalDetailSchema>;

// ── Hazard lexicon (EN + FR, shipped as data) ────────────────────────────────

export const HAZARD_KEYS = [
  'wildfire',
  'smoke_air_quality',
  'flood',
  'hurricane',
  'tornado',
  'severe_storm',
  'winter_ice_storm',
  'earthquake',
  'landslide',
  'power_outage',
  'evacuation',
  'extreme_heat',
  'civil_disturbance',
  'cyber',
  'public_health',
] as const;
export type HazardKey = (typeof HAZARD_KEYS)[number];

export interface HazardLexiconEntry {
  key: HazardKey;
  /** Operator-facing filter labels (3b chip/picker copy). */
  label: string;
  labelFr: string;
  /** Match terms. Phrases allowed; matching is case- and diacritic-insensitive
   *  on word boundaries (see {@link matchHazardKeywords}). */
  en: string[];
  fr: string[];
}

/**
 * The hazard vocabulary as DATA — extend by adding terms here (both the worker
 * match gate and the 3b filter UI read this), never by forking match logic.
 * Terms are deliberately operator-vocabulary (what a reporter/poster writes),
 * not taxonomy codes.
 */
export const HAZARD_LEXICON: readonly HazardLexiconEntry[] = [
  {
    key: 'wildfire',
    label: 'Wildfire',
    labelFr: 'Feu de forêt',
    en: ['wildfire', 'forest fire', 'brush fire', 'grass fire', 'bush fire', 'fire ban'],
    fr: ['feu de forêt', 'incendie de forêt', 'feu de brousse', 'feu de végétation'],
  },
  {
    key: 'smoke_air_quality',
    label: 'Smoke / air quality',
    labelFr: 'Fumée / qualité de l’air',
    en: ['smoke plume', 'air quality warning', 'air quality alert', 'smoke advisory'],
    fr: ['panache de fumée', 'qualité de l’air', 'avertissement de fumée'],
  },
  {
    // R4 PRECISION HARDENING (the same treatment cyber/public_health got): bare
    // 'flood' is DROPPED — it keyed metaphors ("a flood of fans", "flood control
    // probe") that cleared the igniter and produced off-topic "concert" briefs.
    // Kept/added forms are all hazard-qualified: 'flooding' (the -ing form has its
    // own word boundary, so it is unaffected by dropping bare 'flood'), 'flash
    // flood', 'flood warning'/'flood watch' (the official advisory forms), and
    // 'floodwaters'. 'floodlight' etc. never matched (word-boundary matcher).
    key: 'flood',
    label: 'Flood',
    labelFr: 'Inondation',
    en: [
      'flooding',
      'flash flood',
      'flood warning',
      'flood watch',
      'floodwaters',
      'river overflow',
      'storm surge',
    ],
    fr: ['inondation', 'crue', 'débordement de rivière', 'onde de tempête'],
  },
  {
    key: 'hurricane',
    label: 'Hurricane',
    labelFr: 'Ouragan',
    en: ['hurricane', 'tropical storm', 'post-tropical storm'],
    fr: ['ouragan', 'tempête tropicale', 'tempête post-tropicale'],
  },
  {
    key: 'tornado',
    label: 'Tornado',
    labelFr: 'Tornade',
    en: ['tornado', 'funnel cloud'],
    fr: ['tornade', 'nuage en entonnoir'],
  },
  {
    key: 'severe_storm',
    label: 'Severe storm',
    labelFr: 'Orage violent',
    en: ['severe thunderstorm', 'hailstorm', 'windstorm', 'derecho'],
    fr: ['orage violent', 'tempête de grêle', 'tempête de vent'],
  },
  {
    key: 'winter_ice_storm',
    label: 'Winter / ice storm',
    labelFr: 'Tempête hivernale / verglas',
    en: ['ice storm', 'freezing rain', 'blizzard', 'snowstorm', 'snow squall'],
    fr: ['tempête de verglas', 'pluie verglaçante', 'blizzard', 'tempête de neige', 'bourrasque de neige'],
  },
  {
    key: 'earthquake',
    label: 'Earthquake',
    labelFr: 'Séisme',
    en: ['earthquake', 'seismic', 'aftershock'],
    fr: ['séisme', 'tremblement de terre', 'réplique sismique'],
  },
  {
    key: 'landslide',
    label: 'Landslide',
    labelFr: 'Glissement de terrain',
    en: ['landslide', 'mudslide', 'rockslide', 'debris flow'],
    fr: ['glissement de terrain', 'coulée de boue', 'éboulement'],
  },
  {
    key: 'power_outage',
    label: 'Power outage',
    labelFr: 'Panne de courant',
    en: ['power outage', 'power cut', 'blackout', 'without power', 'hydro outage'],
    fr: ['panne de courant', 'panne d’électricité', 'sans électricité', 'panne majeure'],
  },
  {
    key: 'evacuation',
    label: 'Evacuation',
    labelFr: 'Évacuation',
    en: ['evacuation', 'evacuate', 'evacuated', 'evacuees', 'shelter in place'],
    fr: ['évacuation', 'évacuer', 'évacués', 'confinement sur place'],
  },
  {
    key: 'extreme_heat',
    label: 'Extreme heat',
    labelFr: 'Chaleur extrême',
    en: ['heat wave', 'heatwave', 'extreme heat', 'heat warning', 'heat dome'],
    fr: ['vague de chaleur', 'chaleur extrême', 'avertissement de chaleur', 'dôme de chaleur'],
  },
  {
    // #184 igniter target — a GOC-monitored category with NO ingested official
    // feed (pre-official detection is the igniter's value). Added as lexicon
    // DATA per the locked extension rule, never a schema change. Terms list
    // singular+plural explicitly (matching is word-boundary, not stemmed).
    key: 'civil_disturbance',
    label: 'Civil disturbance',
    labelFr: 'Troubles civils',
    en: ['riot', 'riots', 'civil unrest', 'unrest', 'protest', 'protests', 'protesters', 'looting'],
    fr: ['émeute', 'émeutes', 'troubles civils', 'manifestation', 'manifestations', 'manifestants', 'pillage'],
  },
  {
    // Social-first hazard (#296 social-surge wire) — no sensor sees a cyber
    // incident; chatter IS the signal ("the no-sensor-sees-it" case). Terms are
    // cyber-SPECIFIC on purpose, precision-over-recall: bare 'outage' / bare
    // 'virus' are omitted, and the IT-failure terms are qualified ('it systems
    // outage' / 'systems taken offline', NOT a bare 'system outage') so a pure
    // grid/power story is NOT mislabelled cyber — see the no-collision test.
    // 'network outage' is the one soft term, kept as telecom/IT-coded. CCCS
    // cyber alerts are the authoritative corroborating leg (#297 backstop) —
    // this key is detection, never confirmation.
    key: 'cyber',
    label: 'Cyber incident',
    labelFr: 'Cyberincident',
    en: [
      'ransomware', 'cyberattack', 'cyber attack', 'cyber-attack', 'cyber incident',
      'data breach', 'computer virus', 'malware', 'network outage', 'it systems outage',
      'systems taken offline', 'denial of service', 'ddos attack',
    ],
    fr: [
      'rançongiciel', 'cyberattaque', 'cyberattaques', 'cyberincident',
      'fuite de données', 'violation de données', 'virus informatique',
      'logiciel malveillant', 'panne informatique', 'attaque informatique',
    ],
  },
  {
    // Social-first hazard (#296 social-surge wire) — outbreaks surface on social
    // chatter before official bulletins land. Precision-over-recall: bare
    // 'outbreak' (→ "outbreak of violence/laughter") and bare 'quarantine'
    // (→ casual + customs/agricultural) are OMITTED in favour of disease-
    // qualified forms; bare 'virus' is omitted (collides with computer virus +
    // viral-content). 'epidemic'/'pandemic' are KEPT — predominantly literal in
    // hazard news, with accepted residual metaphor noise on an ambient wire.
    // Public Health Ontario + GoC recalls/safety alerts are the authoritative
    // corroborating leg (#297 backstop) — detection, never confirmation.
    key: 'public_health',
    label: 'Public health',
    labelFr: 'Santé publique',
    en: [
      'disease outbreak', 'measles', 'measles outbreak', 'flu outbreak', 'covid outbreak',
      'norovirus outbreak', 'e. coli outbreak', 'epidemic', 'pandemic',
      'public health emergency', 'public health advisory', 'quarantine order',
      'public health quarantine', 'avian flu', 'bird flu', 'h5n1', 'salmonella',
      'listeria', 'whooping cough',
    ],
    fr: [
      'éclosion de rougeole', 'éclosion de grippe', 'éclosion de covid',
      'éclosion de listériose', 'flambée épidémique', 'épidémie', 'pandémie',
      'rougeole', 'urgence de santé publique', 'mise en quarantaine',
      'grippe aviaire', 'maladie contagieuse',
    ],
  },
];

/** Lowercase + strip diacritics + unify curly apostrophes, so "Feu de Forêt"
 *  and "feu de foret" and "l'air"/"l’air" all match the same terms. */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’‘]/g, "'");
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** One compiled word-boundary alternation per lexicon entry, built lazily once
 *  (terms are normalized the same way as the input text). */
let compiled: Array<{ key: HazardKey; re: RegExp }> | null = null;
function compiledLexicon(): Array<{ key: HazardKey; re: RegExp }> {
  if (!compiled) {
    compiled = HAZARD_LEXICON.map((entry) => {
      const terms = [...entry.en, ...entry.fr]
        .map((t) => escapeRegExp(normalize(t)))
        // Longest first so "forest fire" wins over a hypothetical "fire" prefix.
        .sort((a, b) => b.length - a.length);
      return { key: entry.key, re: new RegExp(`\\b(?:${terms.join('|')})\\b`) };
    });
  }
  return compiled;
}

/**
 * Match a free-text blob (title + body, any mix of EN/FR) against the lexicon.
 * Returns the canonical hazard keys, in HAZARD_LEXICON order, deduped. Pure +
 * language-agnostic — the EN and FR term lists are matched together, so a
 * caller never branches on language. Empty array = not a hazard item (the
 * ingest gate drops it).
 */
export function matchHazardKeywords(text: string): HazardKey[] {
  const haystack = normalize(text);
  const keys: HazardKey[] = [];
  for (const { key, re } of compiledLexicon()) {
    if (re.test(haystack)) keys.push(key);
  }
  return keys;
}

/** Lexicon entry lookup for surfaces that render labels from stored keys. */
export function findHazardLexiconEntry(key: string): HazardLexiconEntry | undefined {
  return HAZARD_LEXICON.find((e) => e.key === key);
}

// ── Canada marker (the world-scope noise gate — #563) ────────────────────────

/**
 * A coarse "is this story ABOUT Canada?" gazetteer, shipped as DATA next to the
 * hazard lexicon (#563 noise cut). It is the marker the ingest gate demands from
 * a WORLD-SCOPE feed — the national outlets whose feeds carry international
 * hazard news (a National Post California-wildfire story, a Global News Australia
 * flood) alongside Canadian coverage. A world-scope row is admitted ONLY when its
 * title/body names a Canadian province, territory, "Canada"/"Canadian", or a
 * major Canadian city (OR the row already carries a Canadian point — see the
 * ingest gate). REGIONAL/local feeds are inherently in-scope and never consult
 * this marker.
 *
 * Deliberately CONSERVATIVE on recall vs precision: it matches the strong,
 * unambiguous markers (full province names + FR equivalents + the dotted
 * abbreviations reporters actually write + Canada terms + a curated set of
 * distinctly-Canadian cities). Bare two-letter abbreviations (ON/BC/NS…) are
 * OMITTED — "ON"/"PE"/"NU" collide with common English words and would re-admit
 * the noise this gate exists to drop. A genuine Canadian hazard story virtually
 * always names a province or a major city; the rare national-feed item that names
 * only a tiny place is picked up instead by the F1 gazetteer point (which admits
 * it as Canada-located) or by the same story on a regional feed.
 */
export const CANADA_MARKER_TERMS: readonly string[] = [
  // Country
  'canada', 'canadian', 'canadien', 'canadienne', 'canadiens', 'canadiennes',
  // Provinces / territories — full names (EN + FR)
  'ontario', 'quebec', 'british columbia', 'colombie-britannique', 'alberta',
  'manitoba', 'saskatchewan', 'nova scotia', 'nouvelle-ecosse', 'new brunswick',
  'nouveau-brunswick', 'newfoundland', 'labrador', 'terre-neuve',
  'prince edward island', 'ile-du-prince-edouard', 'yukon',
  'northwest territories', 'territoires du nord-ouest', 'nunavut',
  // Dotted province abbreviations reporters actually write (word-boundary safe)
  'b.c', 'n.s', 'n.b', 'n.l', 'p.e.i', 'n.w.t',
  // Major / distinctly-Canadian cities (curated; ambiguous ones like London,
  // Kingston, Windsor are omitted — they collide with UK/Jamaica place names)
  'toronto', 'ottawa', 'montreal', 'vancouver', 'calgary', 'edmonton',
  'winnipeg', 'halifax', 'saskatoon', 'regina', 'gatineau', 'mississauga',
  'brampton', 'kelowna', 'kamloops', 'yellowknife', 'whitehorse', 'iqaluit',
  'fredericton', 'charlottetown', 'nanaimo', 'kitchener-waterloo', 'thunder bay',
];

let canadaMarkerRe: RegExp | null = null;
function canadaMarkerRegex(): RegExp {
  if (!canadaMarkerRe) {
    const terms = CANADA_MARKER_TERMS.map((t) => escapeRegExp(normalize(t))).sort(
      (a, b) => b.length - a.length,
    );
    canadaMarkerRe = new RegExp(`\\b(?:${terms.join('|')})\\b`);
  }
  return canadaMarkerRe;
}

/**
 * True when a free-text blob (title + body, EN/FR) names Canada — the world-scope
 * noise gate (#563). Normalized the same way as the hazard lexicon (lowercase +
 * de-diacritic + curly-apostrophe unify), so "Québec"/"quebec" and "B.C."/"b.c."
 * all match. Pure + language-agnostic.
 */
export function hasCanadaMarker(text: string): boolean {
  if (!text) return false;
  return canadaMarkerRegex().test(normalize(text));
}
